import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Fiber } from '../types';

type DrawKind = 'locked' | 'inspecting';

interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface LockIconRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

const OVERLAY_DPR =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

function InspectorOverlay() {
    const refCanvas = useRef<HTMLCanvasElement>(null);
    const refEventCatcher = useRef<HTMLDivElement>(null);
    const refCurrentRect = useRef<Rect | null>(null);
    const refCurrentLockIconRect = useRef<LockIconRect | null>(null);

    const refCurrentFiber = useRef<Fiber | null>(null);

    useEffect(() => {
        const canvas = refCanvas.current;
        if (!canvas) return;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;

        updateCanvasSize(canvas, ctx);

        window.addEventListener('scroll', handleResizeOrScroll, {
            passive: true,
        });
        window.addEventListener('resize', handleResizeOrScroll, {
            passive: true,
        });
        document.addEventListener('click', handleClick, { capture: true });

        document.addEventListener('inspect-element', handleInspectElement);

        return () => {
            window.removeEventListener('scroll', handleResizeOrScroll);
            window.removeEventListener('resize', handleResizeOrScroll);
            document.removeEventListener('mousemove', handleMouseMove, {
                capture: true,
            });
            document.removeEventListener('click', handleClick, {
                capture: true,
            });

            document.removeEventListener(
                'inspect-element',
                handleInspectElement
            );
        };
    }, []);

    const handleInspectElement = (event) => {
        const canvas = refCanvas.current;
        if (!canvas) return;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;

        const fiber = event.value;
        refCurrentFiber.current = fiber;

        if (fiber) {
            refCurrentRect.current = fiber.stateNode.getBoundingClientRect();
        } else {
            refCurrentRect.current = null;
        }

        drawRect(canvas, ctx, event.lockeType || 'inspecting');
    };

    const handleMouseMove = (e: MouseEvent) => {};

    const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!refCurrentFiber.current) {
            return;
        }

        const event = new Event('inspect-lock');
        document.dispatchEvent(event);

        handleInspectElement({
            value: refCurrentFiber.current,
            lockeType: 'locked',
        });
    };

    const handleResizeOrScroll = () => {
        const canvas = refCanvas.current;
        if (!canvas) return;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;

        updateCanvasSize(canvas, ctx);

        if (!refCurrentFiber.current) {
            return;
        }
        handleInspectElement({ value: refCurrentFiber.current });
    };

    const updateCanvasSize = (
        canvas: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D
    ) => {
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * OVERLAY_DPR;
        canvas.height = rect.height * OVERLAY_DPR;
        ctx.scale(OVERLAY_DPR, OVERLAY_DPR);
        ctx.save();
    };

    const drawLockIcon = (
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        size: number
    ) => {
        ctx.save();
        ctx.strokeStyle = 'white';
        ctx.fillStyle = 'white';
        ctx.lineWidth = 1.5;

        const shackleWidth = size * 0.6;
        const shackleHeight = size * 0.5;
        const shackleX = x + (size - shackleWidth) / 2;
        const shackleY = y;

        ctx.beginPath();
        ctx.arc(
            shackleX + shackleWidth / 2,
            shackleY + shackleHeight / 2,
            shackleWidth / 2,
            Math.PI,
            0,
            false
        );
        ctx.stroke();

        const bodyWidth = size * 0.8;
        const bodyHeight = size * 0.5;
        const bodyX = x + (size - bodyWidth) / 2;
        const bodyY = y + shackleHeight / 2;

        ctx.fillRect(bodyX, bodyY, bodyWidth, bodyHeight);
        ctx.restore();
    };

    const drawStatsPill = (
        ctx: CanvasRenderingContext2D,
        rect: Rect,
        kind: 'locked' | 'inspecting'
    ) => {
        const pillHeight = 24;
        const pillPadding = 8;
        const componentName = kind == 'locked' ? 'locked' : '点击即可锁定🔒';
        let text = componentName;

        ctx.save();
        ctx.font = '12px system-ui, -apple-system, sans-serif';
        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;
        const lockIconSize = kind === 'locked' ? 14 : 0;
        const lockIconPadding = kind === 'locked' ? 6 : 0;
        const pillWidth =
            textWidth + pillPadding * 2 + lockIconSize + lockIconPadding;

        const pillX = rect.left;
        const pillY = rect.top - pillHeight - 4;

        ctx.fillStyle = 'rgb(37, 37, 38, .75)';
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 3);
        ctx.fill();

        if (kind === 'locked') {
            const lockX = pillX + pillPadding;
            const lockY = pillY + (pillHeight - lockIconSize) / 2 + 2;
            drawLockIcon(ctx, lockX, lockY, lockIconSize);
            refCurrentLockIconRect.current = {
                x: lockX,
                y: lockY,
                width: lockIconSize,
                height: lockIconSize,
            };
        } else {
            refCurrentLockIconRect.current = null;
        }

        ctx.fillStyle = 'white';
        ctx.textBaseline = 'middle';
        const textX =
            pillX +
            pillPadding +
            (kind === 'locked' ? lockIconSize + lockIconPadding : 0);
        ctx.fillText(text, textX, pillY + pillHeight / 2);
        ctx.restore();
    };

    const drawRect = (
        canvas: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D,
        kind: DrawKind
    ) => {
        if (!refCurrentRect.current) return;

        const rect = refCurrentRect.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.strokeStyle = 'rgba(142, 97, 227, 0.5)';
        ctx.fillStyle = 'rgba(173, 97, 230, 0.10)';

        if (kind === 'locked') {
            ctx.setLineDash([]);
        } else {
            ctx.setLineDash([4]);
        }

        ctx.lineWidth = 1;
        ctx.fillRect(rect.left, rect.top, rect.width, rect.height);
        ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);

        drawStatsPill(ctx, rect, kind);
    };

    const baseicStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        width: '100vw',
        height: '100vh',
        zIndex: '214748366',
        pointerEvents: 'none',
    };

    return ReactDOM.createPortal(
        <>
            <div
                ref={refEventCatcher}
                style={{
                    ...baseicStyle,
                    zIndex: '214748366',
                }}
            />
            <canvas
                ref={refCanvas}
                style={{
                    ...baseicStyle,
                    zIndex: '214748367',
                }}
            />
        </>,
        document.body
    );
}

export function InspectorOverlayWrapper() {
    let [inspecting, setInspecting] = useState(false);

    useEffect(() => {
        document.addEventListener('inspect-status', handleInspectStatus);

        return () => {
            document.removeEventListener('inspect-status', handleInspectStatus);
        };
    }, []);

    const handleInspectStatus = (event: any) => {
        if (event.value === 'start') {
            setInspecting(true);
        } else {
            setInspecting(false);
        }
    };

    if (inspecting) {
        return <InspectorOverlay />;
    }

    return null;
}
